import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BookOpen, Plus, Pencil, Trash2, Loader2, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Business Library — the books that shape how MaxSC is run.
 * Each card: why it matters, core principles, and exactly how we apply
 * it at the gym. Owner can add/edit/delete; coaches read.
 */
export default function Library() {
    const { user } = useAuth();
    const isOwner = user?.role === "owner";
    const [books, setBooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null | {} (new) | book

    const load = async () => {
        try {
            const r = await axios.get(`${API}/coach/library`, { withCredentials: true });
            setBooks(r.data || []);
        } catch (e) {
            toast.error("Couldn't load the library", { description: e?.response?.data?.detail });
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const remove = async (book) => {
        if (!window.confirm(`Remove "${book.title}" from the library?`)) return;
        try {
            await axios.delete(`${API}/coach/library/${book.book_id}`, { withCredentials: true });
            toast.success("Removed");
            load();
        } catch (e) {
            toast.error("Couldn't remove", { description: e?.response?.data?.detail });
        }
    };

    return (
        <main data-testid="library-page" className="max-w-4xl mx-auto px-4 sm:px-6 py-8 pt-24 md:pt-8">
            <div className="flex items-center justify-between gap-4 mb-2">
                <h1 className="font-display text-3xl tracking-wider flex items-center gap-3">
                    <BookOpen className="w-7 h-7 text-brand-red" /> BUSINESS LIBRARY
                </h1>
                {isOwner && (
                    <button type="button" data-testid="library-add-book" onClick={() => setEditing({})} className="btn-tactical text-xs">
                        <Plus className="w-3.5 h-3.5" /> Add book
                    </button>
                )}
            </div>
            <p className="text-sm text-zinc-400 mb-8">
                The books that drive how we run this gym — core principles and exactly how we apply them at MaxSC.
            </p>

            {loading ? (
                <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand-red" /></div>
            ) : books.length === 0 ? (
                <div className="text-center py-12 text-zinc-500 border border-dashed border-zinc-800 rounded">No books yet.</div>
            ) : (
                <div className="space-y-4">
                    {books.map((b) => (
                        <BookCard key={b.book_id} book={b} isOwner={isOwner} onEdit={() => setEditing(b)} onDelete={() => remove(b)} />
                    ))}
                </div>
            )}

            {editing !== null && (
                <BookFormModal
                    book={editing.book_id ? editing : null}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }}
                />
            )}
        </main>
    );
}

function BookCard({ book, isOwner, onEdit, onDelete }) {
    const [open, setOpen] = useState(false);
    return (
        <article data-testid={`library-book-${book.book_id}`} className="border border-zinc-800 rounded-lg bg-zinc-950 overflow-hidden">
            <button
                type="button"
                data-testid={`library-book-toggle-${book.book_id}`}
                onClick={() => setOpen((o) => !o)}
                className="w-full text-left px-5 py-4 flex items-start justify-between gap-4 hover:bg-zinc-900/50 transition-colors"
            >
                <div className="min-w-0">
                    <h2 className="font-display text-xl tracking-wider text-white">{book.title}</h2>
                    <div className="text-xs text-zinc-400 mt-0.5">
                        {book.author}{book.year ? ` · ${book.year}` : ""}
                    </div>
                </div>
                <ChevronDown className={`w-5 h-5 text-zinc-400 flex-shrink-0 mt-1 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div className="px-5 pb-5 space-y-4 border-t border-zinc-900 pt-4">
                    {book.summary && <p className="text-sm text-zinc-300 leading-relaxed">{book.summary}</p>}
                    {(book.principles || []).length > 0 && (
                        <div>
                            <div className="text-[10px] tracking-[0.25em] uppercase text-brand-red mb-2">Core principles</div>
                            <ul className="space-y-2">
                                {book.principles.map((p, i) => (
                                    <li key={i} className="text-sm text-zinc-300 leading-relaxed pl-4 border-l-2 border-zinc-800">{p}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {(book.applications || []).length > 0 && (
                        <div>
                            <div className="text-[10px] tracking-[0.25em] uppercase text-emerald-400 mb-2">How we use it at MaxSC</div>
                            <ul className="space-y-1.5">
                                {book.applications.map((a, i) => (
                                    <li key={i} className="text-sm text-zinc-300 leading-relaxed flex gap-2">
                                        <span className="text-emerald-500">→</span>{a}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {isOwner && (
                        <div className="flex gap-2 pt-1">
                            <button type="button" data-testid={`library-edit-${book.book_id}`} onClick={onEdit} className="btn-ghost-tactical text-xs">
                                <Pencil className="w-3 h-3" /> Edit
                            </button>
                            <button type="button" data-testid={`library-delete-${book.book_id}`} onClick={onDelete} className="btn-ghost-tactical text-xs text-zinc-400 hover:text-brand-red">
                                <Trash2 className="w-3 h-3" /> Remove
                            </button>
                        </div>
                    )}
                </div>
            )}
        </article>
    );
}

function BookFormModal({ book, onClose, onSaved }) {
    const [title, setTitle] = useState(book?.title || "");
    const [author, setAuthor] = useState(book?.author || "");
    const [year, setYear] = useState(book?.year || "");
    const [summary, setSummary] = useState(book?.summary || "");
    const [principles, setPrinciples] = useState((book?.principles || []).join("\n"));
    const [applications, setApplications] = useState((book?.applications || []).join("\n"));
    const [saving, setSaving] = useState(false);

    const save = async () => {
        if (!title.trim()) { toast.error("Title is required"); return; }
        setSaving(true);
        const payload = {
            title, author,
            year: year ? Number(year) : null,
            summary,
            principles: principles.split("\n").filter((s) => s.trim()),
            applications: applications.split("\n").filter((s) => s.trim()),
        };
        try {
            if (book) {
                await axios.patch(`${API}/coach/library/${book.book_id}`, payload, { withCredentials: true });
            } else {
                await axios.post(`${API}/coach/library`, payload, { withCredentials: true });
            }
            toast.success(book ? "Updated" : "Added to the library");
            onSaved();
        } catch (e) {
            toast.error("Couldn't save", { description: e?.response?.data?.detail });
        } finally {
            setSaving(false);
        }
    };

    const field = "w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm text-white focus:border-brand-red outline-none";
    return (
        <div className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto">
            <div data-testid="library-book-form" className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-xl my-8">
                <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
                    <h3 className="text-lg font-semibold">{book ? "Edit book" : "Add a book"}</h3>
                    <button type="button" onClick={onClose} className="text-zinc-300 hover:text-white">×</button>
                </div>
                <div className="p-6 space-y-3">
                    <input data-testid="book-title-input" className={field} placeholder="Title *" value={title} onChange={(e) => setTitle(e.target.value)} />
                    <div className="flex gap-3">
                        <input data-testid="book-author-input" className={field} placeholder="Author" value={author} onChange={(e) => setAuthor(e.target.value)} />
                        <input className={`${field} max-w-28`} placeholder="Year" inputMode="numeric" value={year} onChange={(e) => setYear(e.target.value.replace(/\D/g, ""))} />
                    </div>
                    <textarea className={`${field} min-h-20`} placeholder="Why this book matters for the gym" value={summary} onChange={(e) => setSummary(e.target.value)} />
                    <textarea className={`${field} min-h-28`} placeholder={"Core principles — one per line"} value={principles} onChange={(e) => setPrinciples(e.target.value)} />
                    <textarea className={`${field} min-h-24`} placeholder={"How we use it at MaxSC — one per line"} value={applications} onChange={(e) => setApplications(e.target.value)} />
                    <div className="flex justify-end gap-2 pt-1">
                        <button type="button" onClick={onClose} className="btn-ghost-tactical" disabled={saving}>Cancel</button>
                        <button type="button" data-testid="book-save" onClick={save} disabled={saving} className="btn-tactical disabled:opacity-50">
                            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
